import { useEffect, useState } from 'react';

// Types
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Holding from '@/types/holding';
import PortfolioHolding from '@/types/portfolio-holding';
import LocalizedString from '@/types/localized-string';

// Utils
import { HOLDING_TYPES } from '@/utils/portfolios';

interface UseAssetTypeCacheProps {
  portfolioHoldings: PortfolioHolding[];
  getHoldingData: (holding: PortfolioHolding) => Promise<Holding>;
}

/**
 * Custom hook to fetch and cache assetType for funds and stocks in a portfolio.
 * This is needed because the backend doesn't include assetType in portfolio holdings.
 *
 * @param portfolioHoldings - Array of portfolio holdings
 * @param getHoldingData - Function to fetch holding data by portfolio holding
 * @returns Map of holding IDs to their assetType
 */
export function useAssetTypeCache({ portfolioHoldings, getHoldingData }: UseAssetTypeCacheProps): Map<string, LocalizedString> {
  const [assetTypeCache, setAssetTypeCache] = useState<Map<string, LocalizedString>>(new Map());

  useEffect(() => {
    const fetchAssetTypes = async () => {
      const newCache = new Map<string, LocalizedString>();

      await Promise.all(
        portfolioHoldings.map(async holding => {
          const holdingId = holding.original.id;
          const holdingType = holding.original.type;

          // Only fetch for funds and stocks that have an ID
          if (!holdingId || (holdingType !== HOLDING_TYPES.FUND && holdingType !== HOLDING_TYPES.STOCK)) {
            return;
          }

          try {
            const data = await getHoldingData(holding);

            // Only Fund and Stock types have assetType (Alt doesn't)
            const { assetType } = data as Fund | Stock;
            if (assetType) {
              newCache.set(holdingId, assetType);
            }
          } catch (error) {
            console.warn(`Failed to fetch assetType for holding ${holdingId}:`, error);
          }
        }),
      );

      setAssetTypeCache(newCache);
    };

    if (portfolioHoldings.length > 0) {
      fetchAssetTypes();
    }
  }, [portfolioHoldings, getHoldingData]);

  return assetTypeCache;
}
